/**
 * lsStore.js — Compressed localStorage utility (BUG-FF-012)
 *
 * Problem: Corps scenarios fill localStorage to ~4.94 MB / 5 MB limit,
 * permanently blocking Tab 6 baseline storage.
 *
 * Fix: LZ-string compression via compressToUTF16 / decompressFromUTF16.
 * Achieves 60–80% reduction on JSON data; a 2 MB scenario becomes ~400–800 KB.
 *
 * Backward compatibility: lsRead() tries decompression first. If the result
 * is null (legacy uncompressed string), it falls back to plain JSON.parse.
 * Legacy data is transparently readable; new writes are always compressed.
 *
 * Prefix: compressed values are stored with a 'z:' prefix to distinguish
 * them from legacy plain JSON, making the fallback detection unambiguous.
 */

import LZString from 'lz-string';

const COMPRESSED_PREFIX = 'z:';

/**
 * Write a value to localStorage with LZ-string compression.
 * Returns true on success, false on QuotaExceededError or other failure.
 */
export function lsWrite(key, value) {
  try {
    const json = JSON.stringify(value);
    const compressed = COMPRESSED_PREFIX + LZString.compressToUTF16(json);
    localStorage.setItem(key, compressed);
    return true;
  } catch {
    return false;
  }
}

/**
 * Read and decompress a value from localStorage.
 * Handles both compressed (z: prefix) and legacy plain-JSON values.
 * Returns null on missing key, parse failure, or decompression failure.
 */
export function lsRead(key) {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return null;

    if (raw.startsWith(COMPRESSED_PREFIX)) {
      // New compressed format
      const decompressed = LZString.decompressFromUTF16(raw.slice(COMPRESSED_PREFIX.length));
      if (decompressed === null) return null; // corrupt / truncated
      return JSON.parse(decompressed);
    }

    // Legacy plain JSON — read as-is
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Remove a key from localStorage.
 */
export function lsRemove(key) {
  try {
    localStorage.removeItem(key);
  } catch {
    // Nothing meaningful to do
  }
}

/**
 * Estimate current localStorage usage in bytes (approximation).
 * Returns { used, total, keys } where used/total are in bytes.
 */
export function lsUsageBytes() {
  try {
    let used = 0;
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      const v = localStorage.getItem(k) || '';
      // UTF-16: each char = 2 bytes
      used += (k.length + v.length) * 2;
      keys.push({ key: k, bytes: (k.length + v.length) * 2 });
    }
    return { used, total: 5 * 1024 * 1024, keys };
  } catch {
    return { used: 0, total: 5 * 1024 * 1024, keys: [] };
  }
}
